import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import AuthContext from '../context/AuthContext';
import { ArrowLeft, Eye, EyeOff, RefreshCw, UserPlus, Save } from 'lucide-react';

const ROLES = [
  { value: 'department_admin', label: 'Company Admin' },
  { value: 'field_investigator', label: 'Field Investigator' },
  { value: 'skip_tracer', label: 'Skip Tracer' },
  { value: 'transporter', label: 'Transporter' },
];

/**
 * Phase B — Team Member Add/Edit routed pages (was a shadcn Dialog).
 * Routes:
 *   /team/new         → create
 *   /team/:userId/edit → edit existing
 */
const TeamMemberFormPage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { user: currentUser } = useContext(AuthContext);
  const token = localStorage.getItem('nexus_token');
  const isEdit = Boolean(userId);

  const [form, setForm] = useState({
    username: '', password: '', email: '', name: '', phone: '',
    role: 'field_investigator', title: '', department: '', badge_number: '',
    newPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loadUser = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/company/investigators`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = res.ok ? await res.json() : [];
      const u = list.find(x => x.id === userId);
      if (!u) throw new Error('User not found');
      setForm({
        username: u.username || '',
        password: '',
        email: u.email || '',
        name: u.name || '',
        phone: u.phone || '',
        role: u.role || 'field_investigator',
        title: u.title || '',
        department: u.department || '',
        badge_number: u.badge_number || '',
        newPassword: '',
      });
    } catch (e) {
      setError(e.message || 'Could not load user');
    } finally {
      setLoading(false);
    }
  }, [isEdit, userId, token]);

  useEffect(() => { loadUser(); }, [loadUser]);

  const handleCreate = async () => {
    setError('');
    if (!form.username || !form.password || !form.email || !form.name) {
      setError('Username, password, email and name are required');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, company_id: currentUser.company_id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      toast({ title: 'Team member created', description: `${form.name} can now sign in.` });
      navigate('/team');
    } catch (e) {
      setError(e.message || 'Failed to create user');
    } finally {
      setBusy(false);
    }
  };

  const handleUpdate = async () => {
    setError('');
    if (!form.name || !form.email) {
      setError('Name and email are required');
      return;
    }
    setBusy(true);
    try {
      const update = {
        name: form.name, email: form.email, phone: form.phone,
        role: form.role, title: form.title, department: form.department,
        badge_number: form.badge_number,
      };
      if (form.newPassword) update.password = form.newPassword;
      const res = await fetch(`${BACKEND_URL}/api/users/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(update),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      toast({
        title: 'User updated',
        description: form.newPassword ? 'Password reset — share the new password with the user.' : 'Profile saved.',
      });
      navigate('/team');
    } catch (e) {
      setError(e.message || 'Failed to update user');
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (isEdit) handleUpdate();
    else handleCreate();
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-300">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading team member…
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="team-member-form-page">
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          className="border-slate-600 text-slate-200 hover:bg-[#182136]"
          onClick={() => navigate('/team')}
          data-testid="team-form-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Team
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-white">
            {isEdit ? 'Edit Team Member' : 'Add Team Member'}
          </h1>
          <p className="text-sm text-slate-400">
            {isEdit ? 'Update user profile, role, and optionally reset password.' : 'Create a new investigator, skip tracer, or transporter for your company.'}
          </p>
        </div>
      </div>

      <Card className="bg-[#131A2A] border-[#334155]">
        <CardContent className="p-6">
          <form onSubmit={onSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-200">Full Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="bg-[#0B0F19] border-[#334155] text-white" placeholder="John Doe" data-testid="team-name-input" />
              </div>
              <div>
                <Label className="text-slate-200">Username {isEdit ? '' : '*'}</Label>
                <Input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })}
                  disabled={isEdit}
                  className="bg-[#0B0F19] border-[#334155] text-white disabled:opacity-60" placeholder="johndoe" data-testid="team-username-input" />
              </div>
              <div>
                <Label className="text-slate-200">Email *</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  className="bg-[#0B0F19] border-[#334155] text-white" placeholder="john@company.com" data-testid="team-email-input" />
              </div>
              <div>
                <Label className="text-slate-200">{isEdit ? 'New Password (optional)' : 'Password *'}</Label>
                <div className="flex gap-1">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={isEdit ? form.newPassword : form.password}
                    onChange={e => setForm(isEdit
                      ? { ...form, newPassword: e.target.value }
                      : { ...form, password: e.target.value })}
                    className="bg-[#0B0F19] border-[#334155] text-white"
                    placeholder={isEdit ? 'Leave blank to keep current' : 'Password'}
                    data-testid="team-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-slate-400 hover:text-white px-2">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-slate-200">Phone</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                  className="bg-[#0B0F19] border-[#334155] text-white" placeholder="(555) 000-0000" />
              </div>
              <div>
                <Label className="text-slate-200">Role *</Label>
                <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                  <SelectTrigger className="bg-[#0B0F19] border-[#334155] text-white" data-testid="team-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#131A2A] border-[#334155] text-white">
                    {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-200">Title / Position</Label>
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                  className="bg-[#0B0F19] border-[#334155] text-white" placeholder="Senior Investigator" data-testid="team-title-input" />
              </div>
              <div>
                <Label className="text-slate-200">Department</Label>
                <Input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })}
                  className="bg-[#0B0F19] border-[#334155] text-white" placeholder="Investigations" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-slate-200">Badge / Certification Number</Label>
                <Input value={form.badge_number} onChange={e => setForm({ ...form, badge_number: e.target.value })}
                  className="bg-[#0B0F19] border-[#334155] text-white" placeholder="PI-2025-001" data-testid="team-badge-input" />
              </div>
            </div>

            {error && (
              <div className="px-3 py-2 rounded border border-red-500/50 bg-red-500/10 text-sm text-red-200" data-testid="team-form-error">
                {error}
              </div>
            )}

            <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => navigate('/team')}
                className="border-slate-600 text-slate-200 hover:bg-[#182136]">Cancel</Button>
              <Button type="submit" disabled={busy}
                className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white disabled:opacity-60"
                data-testid={isEdit ? 'submit-team-edit-btn' : 'submit-team-member-btn'}>
                {busy ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />{isEdit ? 'Saving…' : 'Creating…'}</>
                ) : (
                  <>{isEdit ? <><Save className="w-4 h-4 mr-2" />Save Changes</> : <><UserPlus className="w-4 h-4 mr-2" />Create Team Member</>}</>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamMemberFormPage;
